/**
 * The zip every Office Open XML file is: a CRC and three headers per part, written in the browser
 * with no library — shared by the workbook (export-xlsx.ts) and the Word document (export-docx.ts).
 * Parts are deflated with the browser's own `CompressionStream` where it exists and stored where it
 * does not; Excel and Word open both.
 */
const CRC_TABLE = (() => {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++)
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        t[n] = c >>> 0;
    }
    return t;
})();
export function crc32(bytes) {
    let c = 0xffffffff;
    for (let i = 0; i < bytes.length; i++)
        c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
}
/** Raw deflate through the platform, or null where there is none (then the part is stored). */
async function deflateRaw(bytes) {
    if (typeof CompressionStream === 'undefined')
        return null;
    try {
        const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate-raw'));
        return new Uint8Array(await new Response(stream).arrayBuffer());
    }
    catch {
        return null;
    }
}
/** A zip of the given parts, in order. `compress: false` stores every part, for a reader that inflates nothing. */
export async function zip(parts, at, { compress = true } = {}) {
    // The MS-DOS time and date every entry carries: two-second resolution, years from 1980.
    const time = (at.getHours() << 11) | (at.getMinutes() << 5) | Math.floor(at.getSeconds() / 2);
    const date = ((Math.max(1980, at.getFullYear()) - 1980) << 9) | ((at.getMonth() + 1) << 5) | at.getDate();
    const encoder = new TextEncoder();
    const locals = [];
    const centrals = [];
    let offset = 0;
    for (const part of parts) {
        const name = encoder.encode(part.name);
        const crc = crc32(part.data);
        const deflated = compress ? await deflateRaw(part.data) : null;
        const stored = !deflated || deflated.length >= part.data.length;
        const body = stored ? part.data : deflated;
        const method = stored ? 0 : 8;
        const local = new Uint8Array(30 + name.length);
        const l = new DataView(local.buffer);
        l.setUint32(0, 0x04034b50, true);
        l.setUint16(4, 20, true);
        l.setUint16(6, 0x0800, true); // UTF-8 names
        l.setUint16(8, method, true);
        l.setUint16(10, time, true);
        l.setUint16(12, date, true);
        l.setUint32(14, crc, true);
        l.setUint32(18, body.length, true);
        l.setUint32(22, part.data.length, true);
        l.setUint16(26, name.length, true);
        l.setUint16(28, 0, true);
        local.set(name, 30);
        const central = new Uint8Array(46 + name.length);
        const c = new DataView(central.buffer);
        c.setUint32(0, 0x02014b50, true);
        c.setUint16(4, 20, true);
        c.setUint16(6, 20, true);
        c.setUint16(8, 0x0800, true);
        c.setUint16(10, method, true);
        c.setUint16(12, time, true);
        c.setUint16(14, date, true);
        c.setUint32(16, crc, true);
        c.setUint32(20, body.length, true);
        c.setUint32(24, part.data.length, true);
        c.setUint16(28, name.length, true);
        c.setUint32(42, offset, true);
        central.set(name, 46);
        locals.push(local, body);
        centrals.push(central);
        offset += local.length + body.length;
    }
    const size = centrals.reduce((n, x) => n + x.length, 0);
    const end = new Uint8Array(22);
    const e = new DataView(end.buffer);
    e.setUint32(0, 0x06054b50, true);
    e.setUint16(8, parts.length, true);
    e.setUint16(10, parts.length, true);
    e.setUint32(12, size, true);
    e.setUint32(16, offset, true);
    const all = [...locals, ...centrals, end];
    const out = new Uint8Array(all.reduce((n, x) => n + x.length, 0));
    let at2 = 0;
    for (const x of all) {
        out.set(x, at2);
        at2 += x.length;
    }
    return out;
}
//# sourceMappingURL=zip.js.map